import React, { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from 'shadcn-ui';
import { AccordionProps } from '../Accordion';

const ITEM_VALUE = 'section';

function getStorageKey(pageContext: string) {
  return `accordion-state-${pageContext}`;
}

function readPersistedState(pageContext?: string, sectionId?: string): boolean | undefined {
  if (!pageContext || !sectionId) {
    return undefined;
  }

  try {
    const stored = localStorage.getItem(getStorageKey(pageContext));
    if (stored) {
      const parsed = JSON.parse(stored);
      if (sectionId in parsed) {
        return parsed[sectionId] === true;
      }
    }
  } catch {
    // ignore
  }
  return undefined;
}

function persistState(pageContext: string, sectionId: string, isOpen: boolean) {
  try {
    const key = getStorageKey(pageContext);
    const stored = localStorage.getItem(key);
    const state = stored ? JSON.parse(stored) : {};
    state[sectionId] = isOpen;
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export default function AccordionLayoutWeb(props: AccordionProps) {
  const [value, setValue] = useState<string | undefined>(() => {
    const persistedOpen = readPersistedState(props.pageContext, props.sectionId);
    const initialOpen = persistedOpen ?? (props.defaultOpen !== false);
    return initialOpen ? ITEM_VALUE : undefined;
  });

  const handleValueChange = (newValue: string) => {
    setValue(newValue || undefined);

    if (props.pageContext && props.sectionId) {
      persistState(props.pageContext, props.sectionId, newValue === ITEM_VALUE);
    }
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={value}
      onValueChange={handleValueChange}
    >
      <AccordionItem value={ITEM_VALUE} className="border-b-0">
        <AccordionTrigger
          className="border-l-4 border-blue-500 bg-slate-50 rounded-none gap-3"
        >
          <span className="font-heading font-semibold text-gray-800 uppercase text-app-base tracking-tight">
            {props.title}
          </span>
          {props.count !== undefined && (
            <span className="text-gray-500 font-normal text-app-md normal-case">
              ({props.count})
            </span>
          )}
        </AccordionTrigger>
        <AccordionContent>
          {props.children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
